import { next, prev } from "@/assets/images";
import { Button } from "@/components";
import { useDesignMaxTheme } from "@/packages";
import { useNewNavigate } from "@/shared/client";
import { FONT } from "@/styles";
import { Box, Center, Flex, Grid, Progress, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import { type OtherRoomSlideProps } from "./ohterRoomSlide.type";

export const OtherRoomSlide = ({ currentRooms }: OtherRoomSlideProps) => {
    const { MEDIAS, COLORS } = useDesignMaxTheme();
    const { isMobile, isLaptop } = MEDIAS();
    const carouselRef = useRef<Carousel>(null);
    const { onNavigate } = useNewNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);
    const groupedRoomList = isMobile
        ? currentRooms.map((room) => [room])
        : currentRooms.reduce(
              (result, room, index) => {
                  if (index % 3 === 0) result.push([]);
                  result[result.length - 1].push(room);
                  return result;
              },
              [] as Array<(typeof currentRooms)[number][]>,
          );

    const roomCardImgHeight = isLaptop ? "184PX" : "320px";
    const roomNamePCFontSize = isLaptop ? 20 : 24;

    const handlePrev = () => {
        if (carouselRef.current) {
            if (currentIndex === 0) {
                carouselRef.current.moveTo(groupedRoomList.length - 1);
                setCurrentIndex(groupedRoomList.length - 1);
            } else {
                const newIdx = currentIndex - 1;
                carouselRef.current.moveTo(newIdx);
                setCurrentIndex(newIdx);
            }
        }
    };

    const handleNext = () => {
        if (carouselRef.current) {
            if (currentIndex === groupedRoomList.length - 1) {
                carouselRef.current.moveTo(0);
                setCurrentIndex(0);
            } else {
                const newIdx = currentIndex + 1;
                carouselRef.current.moveTo(newIdx);
                setCurrentIndex(newIdx);
            }
        }
    };

    const handleChange = (index: number) => {
        setCurrentIndex(index);
    };

    return (
        <Flex flex="1" w="100%" flexDir="column" rowGap="16px">
            <Carousel
                onChange={handleChange}
                infiniteLoop
                ref={carouselRef}
                showArrows={false}
                showStatus={false}
                showThumbs={false}
                showIndicators={false}
            >
                {groupedRoomList.map((group) => (
                    <Grid
                        key={Math.random()}
                        w="100%"
                        gridTemplateColumns={isMobile ? "repeat(1,1fr)" : "repeat(3,1fr)"}
                        columnGap={isMobile ? "0" : "24px"}
                        h="auto"
                    >
                        {group.map((room) => (
                            <Flex flexDir="column" key={room.roomIdx} h="100%" rowGap="8px">
                                <Box
                                    position="relative"
                                    w="100%"
                                    maxH={isMobile ? "263px" : "none"}
                                    height={isMobile ? "263px" : roomCardImgHeight}
                                    background={`url(${room.imgUrl})no-repeat center/cover`}
                                    onClick={() => isMobile && onNavigate({ path: `/rooms/${room.roomIdx}` })}
                                >
                                    {!isMobile && (
                                        <Center
                                            position="absolute"
                                            w="100%"
                                            h="100%"
                                            background="blackAlpha.400"
                                            opacity="0"
                                            _hover={{
                                                opacity: isMobile ? "0" : "1",
                                            }}
                                            sx={{
                                                "& > button": {
                                                    cursor: "pointer",
                                                    w: "auto",
                                                    h: "48px",
                                                    borderRadius: "38px",
                                                    color: COLORS.white,
                                                    borderColor: COLORS.white,
                                                    ...FONT("pretendard", 14, 300),
                                                },
                                            }}
                                        >
                                            <Button onClick={() => onNavigate({ path: `/rooms/${room.roomIdx}` })}>
                                                자세히보기
                                            </Button>
                                        </Center>
                                    )}
                                </Box>
                                <Center flexDir="column" flex="1">
                                    <Text {...FONT("notoSerif", isMobile ? 16 : roomNamePCFontSize, 500)}>
                                        {room.roomDetailInfo.name}
                                    </Text>
                                    <Box
                                        sx={{
                                            "& p": {
                                                ...FONT("pretendard", isMobile ? 14 : 16, 300),
                                            },
                                        }}
                                    >
                                        <Text>
                                            #기준{room.roomDetailInfo.personOfCapacity.min}인 #최대
                                            {room.roomDetailInfo.personOfCapacity.max}인
                                        </Text>
                                        <Text>{room.roomDetailInfo.structure}</Text>
                                    </Box>
                                </Center>
                            </Flex>
                        ))}
                    </Grid>
                ))}
            </Carousel>
            <Flex
                w="100%"
                alignItems="center"
                justifyContent="space-between"
                p="0 24px"
                columnGap="24px"
                sx={{
                    "& p": {
                        ...FONT("pretendard", isLaptop ? 12 : 14, 500),
                    },
                }}
            >
                <Center
                    w={isLaptop ? "48px" : "80px"}
                    h={isLaptop ? "48px" : "80px"}
                    flexDir="column"
                    color={COLORS.gray[400]}
                    cursor="pointer"
                    onClick={handlePrev}
                >
                    <Box
                        w={isLaptop ? "28px" : "56px"}
                        h={isLaptop ? "8px" : "17px"}
                        background={`url(${prev})no-repeat center/cover`}
                    />
                    <Text>PREV</Text>
                </Center>
                <Progress
                    w="100%"
                    h="2px"
                    value={(currentIndex + 1) * (100 / groupedRoomList.length)}
                    size="sm"
                    colorScheme="blackAlpha"
                />
                <Center
                    w={isLaptop ? "48px" : "80px"}
                    h={isLaptop ? "48px" : "80px"}
                    flexDir="column"
                    color={COLORS.gray[400]}
                    cursor="pointer"
                    onClick={handleNext}
                >
                    <Box
                        w={isLaptop ? "28px" : "56px"}
                        h={isLaptop ? "8px" : "17px"}
                        background={`url(${next})no-repeat center/cover`}
                    />
                    <Text>NEXT</Text>
                </Center>
            </Flex>
        </Flex>
    );
};
